import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemPrice from 'LuxKit/List/ListItemPrice'
import { OfferPageActions, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { useCallback, useMemo } from 'react'
import { getCheapestRoomOptions } from 'selectors/offerPage/offerPageSelectors'
import RadioButton from 'LuxKit/Radio/RadioButton'
import { arrayToObject } from 'lib/array/arrayUtils'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

function getOptionPriceLabel(option: App.OfferPackageOptionView, isFromPrice: boolean) {
  if (!option.hotelAvailable) {
    return 'Sold out'
  } else if (!option.capacityValid) {
    return 'Max guests exceeded'
  } if (isFromPrice) {
    return 'From'
  }
  return ''
}

interface Props {
  room?: App.PackageRoomType
  withFlights?: boolean
  isFromPrice: boolean
  packageOptions: Array<App.OfferPackageOptionView>
  onSelect?: () => void
  offer: App.Offer
  bundleOfferId?: string
  currentOfferId?: string
  displayMemberPrice: boolean
}

function OfferCalendarRoomOptions(props: Props) {
  const { room, isFromPrice, withFlights, onSelect, offer, currentOfferId, bundleOfferId, displayMemberPrice, packageOptions } = props
  const dispatch = useOfferPageDispatch()

  const onItemClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const id = e.currentTarget.dataset.roomid
    const option = packageOptions.find(opt => opt.package.roomType?.id === id)
    dispatch({ type: OfferPageActions.setRoomType, room: option?.package.roomType, offerId: currentOfferId })
    onSelect?.()
  }, [dispatch, onSelect, packageOptions, currentOfferId])

  const prices = useMemo(() => {
    return arrayToObject(packageOptions,
      option => option.package.uniqueKey,
      option => {
        if (offer.bundledWithFlightsOnly && !option.flightPrice) {
          return undefined
        }

        if (!option.hotelAvailable || !option.capacityValid) {
          return undefined
        }

        return Math.ceil(((displayMemberPrice ? option.hotelMemberPrice : option.hotelPrice) + (option.propertyFees ?? 0)) + (withFlights ? option.flightPrice : 0))
      },
    )
  }, [displayMemberPrice, offer.bundledWithFlightsOnly, packageOptions, withFlights])

  return (<>
    <ClickableListItem
      title="View availability for all rooms"
      selected={!room}
      onClick={onItemClick}
      startIcon={<RadioButton name="room" checked={!room} />}
    />
    {packageOptions.map(option => option.package.roomType && <ClickableListItem
      key={option.package.roomType.id}
      title={option.package.roomType.name}
      disabled={!option.hotelAvailable || !option.capacityValid}
      data-roomid={option.package.roomType.id}
      selected={room?.id === option.package.roomType.id}
      onClick={onItemClick}
      startIcon={<RadioButton name="room" checked={room?.id === option.package.roomType.id} />}
    >
      <ListItemPrice
        price={!bundleOfferId ? prices[option.package.uniqueKey] : undefined}
        label={getOptionPriceLabel(option, isFromPrice)}
        priceColour={displayMemberPrice ? 'lux-plus' : undefined}
      />
    </ClickableListItem>)}
  </>)
}

export default withOfferPageAndReduxState((currentState, reduxState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    room: state.room,
    bundleOfferId: state.bundleOffer?.id,
    currentOfferId: state.paginator?.currentOfferId,
    packageOptions: getCheapestRoomOptions(state, reduxState),
    withFlights: state.flights,
    isFromPrice: !state.checkInDate,
    offer: state.offer,
    displayMemberPrice: state.displayMemberPrice,
    open: !!state.activeFilter,
  }
}, OfferCalendarRoomOptions, preventRenderWithClosedFilter)
